// Maps the current DOM text selection onto a reader paragraph so we can offer a
// selection menu and, later, splice a reworded phrase back into the segment.

export interface ReaderSelection {
  segmentId: string; // value of the paragraph's data-tts-seg attribute
  text: string; // the exact selected substring
  base: string; // the paragraph's full text (the splice target)
  start: number; // char offset of the selection start within `base`
  end: number; // char offset of the selection end within `base`
  rect: DOMRect; // bounding box of the selection (viewport coords)
}

// Char offset from the start of `root`'s text content up to (node, offset).
// Walks across any nested nodes, so it's robust to inline markup.
function offsetWithin(root: Node, node: Node, offset: number): number {
  const range = document.createRange();
  range.selectNodeContents(root);
  range.setEnd(node, offset);
  return range.toString().length;
}

// Returns the reader paragraph (the [data-tts-seg] element) containing a node,
// or null if the node isn't inside one.
function paragraphOf(node: Node | null): HTMLElement | null {
  let el = node instanceof Element ? node : (node?.parentElement ?? null);
  return el?.closest<HTMLElement>("[data-tts-seg]") ?? null;
}

// Inverse of offsetWithin: resolves a char offset into `root`'s text content to
// a concrete (text node, offset) pair, so a snapped range can be re-applied to
// the live selection. Clamps past-the-end offsets to the last text node.
function locateOffset(
  root: Node,
  target: number,
): { node: Node; offset: number } {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  let acc = 0;
  let last: Node = root;
  for (let n = walker.nextNode(); n; n = walker.nextNode()) {
    const len = n.textContent?.length ?? 0;
    if (target <= acc + len) return { node: n, offset: target - acc };
    acc += len;
    last = n;
  }
  return { node: last, offset: last.textContent?.length ?? 0 };
}

const isWordChar = (ch: string | undefined) => !!ch && /[\p{L}\p{N}]/u.test(ch);

// A word range within a reader paragraph, addressed by char offsets into the
// paragraph's text content.
export interface WordRange {
  para: HTMLElement;
  start: number;
  end: number;
}

// The (text node, offset) under a viewport point, across the two browser APIs
// (WebKit/Blink's caretRangeFromPoint vs the standard caretPositionFromPoint).
function caretFromPoint(
  x: number,
  y: number,
): { node: Node; offset: number } | null {
  const doc = document as Document & {
    caretRangeFromPoint?(x: number, y: number): Range | null;
    caretPositionFromPoint?(
      x: number,
      y: number,
    ): { offsetNode: Node; offset: number } | null;
  };
  if (doc.caretRangeFromPoint) {
    const r = doc.caretRangeFromPoint(x, y);
    return r ? { node: r.startContainer, offset: r.startOffset } : null;
  }
  if (doc.caretPositionFromPoint) {
    const p = doc.caretPositionFromPoint(x, y);
    return p ? { node: p.offsetNode, offset: p.offset } : null;
  }
  return null;
}

// The whole word under a viewport point, as char offsets into its reader
// paragraph. Returns null when the point isn't on a word inside a
// [data-tts-seg] paragraph (whitespace, padding, or outside the reader text) —
// this is how we replace native selection: we hit-test points ourselves.
export function wordRangeAtPoint(x: number, y: number): WordRange | null {
  const caret = caretFromPoint(x, y);
  if (!caret || caret.node.nodeType !== Node.TEXT_NODE) return null;
  const para = paragraphOf(caret.node);
  if (!para?.dataset.ttsSeg) return null;
  const base = para.textContent ?? "";
  const at = offsetWithin(para, caret.node, caret.offset);
  let s = at;
  let e = at;
  while (s > 0 && isWordChar(base[s - 1])) s--;
  while (e < base.length && isWordChar(base[e])) e++;
  return e > s ? { para, start: s, end: e } : null;
}

// A DOM Range over [start, end) of a paragraph's text content, for measuring
// (getBoundingClientRect) or painting the selection highlight.
export function rangeForOffsets(
  para: HTMLElement,
  start: number,
  end: number,
): Range {
  const a = locateOffset(para, start);
  const b = locateOffset(para, end);
  const range = document.createRange();
  range.setStart(a.node, a.offset);
  range.setEnd(b.node, b.offset);
  return range;
}

// Resolves a paragraph + char range into the ReaderSelection the WordingSheet
// consumes. Offsets are normalised (ordered, clamped); returns null for an
// empty or whitespace-only span.
export function readerSelectionFromOffsets(
  para: HTMLElement,
  start: number,
  end: number,
): ReaderSelection | null {
  const segmentId = para.dataset.ttsSeg;
  if (!segmentId) return null;
  const base = para.textContent ?? "";
  const s = Math.max(0, Math.min(start, end));
  const e = Math.min(base.length, Math.max(start, end));
  const text = base.slice(s, e);
  if (!text.trim()) return null;
  return {
    segmentId,
    text,
    base,
    start: s,
    end: e,
    rect: rangeForOffsets(para, s, e).getBoundingClientRect(),
  };
}

// Paints (range) or clears (null) our own selection tint via the CSS Custom
// Highlight API. We suppress the native selection (user-select: none on the
// reader) so mobile browsers don't raise their selection toolbar / contextual
// search over the rewording sheet — so the visible highlight has to be ours.
// A no-op where the API is unavailable: selection still works, just untinted.
const HIGHLIGHT_NAME = "tf-selection";
export function paintSelection(range: Range | null): void {
  const w = window as unknown as {
    CSS?: {
      highlights?: {
        set(k: string, v: unknown): void;
        delete(k: string): void;
      };
    };
    Highlight?: new (range: Range) => unknown;
  };
  const highlights = w.CSS?.highlights;
  if (!highlights || !w.Highlight) return;
  if (range) highlights.set(HIGHLIGHT_NAME, new w.Highlight(range));
  else highlights.delete(HIGHLIGHT_NAME);
}

// Splices `replacement` into `base` over the [start, end) range.
export function spliceText(
  base: string,
  start: number,
  end: number,
  replacement: string,
): string {
  return base.slice(0, start) + replacement + base.slice(end);
}

export interface SourceSentence {
  start: number; // char offset of the sentence within source (incl. trailing space)
  end: number;
  text: string; // trimmed sentence text for display
}

// Splits source prose into sentences, tiling the whole string (trailing
// whitespace belongs to the preceding sentence) so offsets stay contiguous.
// Heuristic — good enough to navigate an approximate original; abbreviations
// may over-split, which is harmless here.
export function splitSentences(source: string): SourceSentence[] {
  const res: SourceSentence[] = [];
  let start = 0;
  for (let i = 0; i < source.length; i++) {
    if (!".!?…".includes(source[i])) continue;
    let j = i + 1;
    while (j < source.length && /["'»”’)\]]/.test(source[j])) j++; // closers
    if (j < source.length && !/\s/.test(source[j])) continue; // mid-token dot
    while (j < source.length && /\s/.test(source[j])) j++; // trailing space
    res.push({ start, end: j, text: source.slice(start, j).trim() });
    start = j;
    i = j - 1;
  }
  if (start < source.length) {
    res.push({ start, end: source.length, text: source.slice(start).trim() });
  }
  return res.filter((s) => s.text.length > 0);
}

// Index into `sentences` most likely matching a translation selection, mapped
// at the *sentence* level rather than by raw character proportion. We find which
// translated sentence the selection falls in, then map that sentence's position
// onto the source sentences proportionally: translated sentence i of n → source
// round(i·(m−1)/(n−1)). This anchors the endpoints exactly (first→first,
// last→last) and interpolates the middle, so selecting the *start* of a sentence
// lands on the start of a whole source sentence instead of mid-sentence (which
// the old char-proportional midpoint did). Mapping several translated sentences
// onto one source sentence (or vice versa) is expected and fine — we just never
// leave the selection pointing at nothing. Falls back to 0.
export function likelySentenceIndex(
  base: string,
  start: number,
  end: number,
  sentences: SourceSentence[],
): number {
  const m = sentences.length;
  if (m === 0) return 0;
  const target = splitSentences(base);
  const n = target.length;
  if (n <= 1) return 0; // single (or no) translated sentence → first source one

  const mid = (start + end) / 2;
  let tIdx = target.findIndex((sn) => mid >= sn.start && mid < sn.end);
  if (tIdx < 0) tIdx = mid <= target[0].start ? 0 : n - 1; // gap → nearest end
  return Math.round((tIdx * (m - 1)) / (n - 1));
}

// Parses the optional sentence-map notation into 0-based source indices per
// translated sentence. "1,3,2,(4,5)" → [[0],[2],[1],[3,4]]; position is the
// translated sentence, value(s) the source sentence(s) it came from. Returns
// null on any malformed input so callers fall back to the heuristic — this is a
// best-effort hint that may be stale, never a hard dependency.
export function parseSentenceMap(
  raw: string | null | undefined,
): number[][] | null {
  if (!raw) return null;
  const groups = raw.match(/\([^()]*\)|[^,()]+/g);
  if (!groups) return null;
  const out: number[][] = [];
  for (const g of groups) {
    const nums = g.replace(/[()]/g, "").split(",");
    const idxs: number[] = [];
    for (const n of nums) {
      const v = Number(n.trim());
      if (!Number.isInteger(v) || v < 1) return null; // 1-based, positive
      idxs.push(v - 1);
    }
    if (idxs.length === 0) return null;
    out.push(idxs);
  }
  return out.length > 0 ? out : null;
}

// Given a translation selection, returns the source sentence index the map
// points at — the *first* of a group, per design. Resolves the selection to a
// translated sentence (which target sentence its midpoint falls in), looks that
// up in the map, and validates the result against the actual source sentences.
// Returns null whenever the map is absent/malformed or any index is out of
// range, so the caller falls back to `likelySentenceIndex`.
export function mappedSentenceIndex(
  base: string,
  start: number,
  end: number,
  sentences: SourceSentence[],
  sentenceMap: string | null | undefined,
): number | null {
  const map = parseSentenceMap(sentenceMap);
  if (!map || sentences.length === 0) return null;

  const targetSentences = splitSentences(base);
  if (targetSentences.length === 0) return null;
  const mid = (start + end) / 2;
  const tIdx = targetSentences.findIndex(
    (sn) => mid >= sn.start && mid < sn.end,
  );
  if (tIdx < 0 || tIdx >= map.length) return null;

  const srcIdx = map[tIdx][0]; // group → first sentence
  return srcIdx >= 0 && srcIdx < sentences.length ? srcIdx : null;
}
